import { IDENTITY_TYPE, LIMITS } from '../constants';
import { IdentifyForm, IdentifyFormErrors, IdentityType } from '../types';

const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
const PAN_SEPARATORS = /[\s\-./_]/g;

export const normalizePhone = (value: string): string => {
	const digits: string = value.replace(/\D/g, '');
	if (digits.length === 12 && digits.startsWith('91')) return digits.slice(2);
	if (digits.length === 11 && digits.startsWith('0')) return digits.slice(1);
	return digits;
};

export const normalizePan = (value: string): string => value.toUpperCase().replace(PAN_SEPARATORS, '');

// Mirrors the server-side checks; the server remains authoritative.
export const validateIdentifier = (identityType: IdentityType, rawValue: string): string | undefined => {
	const value: string = rawValue.trim();
	if (!value) return 'Enter an identifier value';
	if (value.length > LIMITS.IDENTIFIER_MAX) return `Must be at most ${LIMITS.IDENTIFIER_MAX} characters`;

	switch (identityType) {
		case IDENTITY_TYPE.UID:
			return /^\d+$/.test(value) ? undefined : 'Zupee User ID must contain digits only';
		case IDENTITY_TYPE.PHONE: {
			const phone: string = normalizePhone(value);
			return phone.length === 10 && /^[6-9]/.test(phone)
				? undefined
				: 'Enter a 10-digit Indian mobile number starting with 6–9 (optionally prefixed with 91 or 0)';
		}
		case IDENTITY_TYPE.PAN:
			return PAN_REGEX.test(normalizePan(value)) ? undefined : 'PAN must look like ABCDE1234F';
		default:
			return undefined;
	}
};

export const validateIdentifyForm = (form: IdentifyForm): IdentifyFormErrors => {
	const errors: IdentifyFormErrors = {};
	const ticketId: string = form.ticketId.trim();
	const reason: string = form.reason.trim();

	if (!ticketId) {
		errors.ticketId = 'Legal ticket ID is required';
	} else if (ticketId.length > LIMITS.TICKET_ID_MAX) {
		errors.ticketId = `Must be at most ${LIMITS.TICKET_ID_MAX} characters`;
	}

	if (!reason) {
		errors.reason = 'Reason is required';
	} else if (reason.length < LIMITS.REASON_MIN || reason.length > LIMITS.REASON_MAX) {
		errors.reason = `Reason must be ${LIMITS.REASON_MIN}–${LIMITS.REASON_MAX} characters`;
	}

	const valueError: string | undefined = validateIdentifier(form.identityType, form.value);
	if (valueError) errors.value = valueError;

	return errors;
};
